import { StatusCodes } from "http-status-codes";
import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import { useDataLoader } from "react-remote-data-hooks/dist/useDataLoader";
import {
  ICalendarEventResponse,
  CalendarApiResponse,
  IShowroomShift,
} from "../..";
import api from "../../../api";
import ContainerFitLoader from "../../../components/loader/container-fit-loader";
import { IFormFilterData } from "../../../containers/leads/components/activities/edit-event-popup";
import { DateFormats, formatDate } from "../../utils";
import {
  BigCalendarComponent,
  IBigCalendarEvent,
  IBigCalendarResource,
} from "./big-calendar-component";
import { CalendarHeaderDate } from "./calendar-header-date";
import {
  parseAssigneesBigCalendarResources,
  parseCalendarEventsToBigCalendarEvents,
  parseShowroomShifts,
  parseVehicleEventsToBigCalendarEvents,
  parseVehiclesBigCalendarResources,
} from "./calendar-mapper-utils";
import { CalendarStaffImageOnlyHeader } from "./calendar-staff-image-only-header";
import { CalendarVehicleHeader } from "./calendar-vehicle-header";
import { useAppSettings } from "../../hooks/useAppSettings";
import { combineDateAndTime } from "../../../utils/CombineDateAndTime";

interface IEditEventCalendarWraperProps {
  tempEvent?: IBigCalendarEvent;
  selectedDate: Date;
  eventType?: string;
  onDateChange: (date: Date) => void;
  formFilter?: IFormFilterData;
  isVehicleOnlyView?: boolean;
  onSelectSlot: (
    start: Date,
    end: Date,
    resource?: IBigCalendarResource
  ) => void;
  onSlotClick?: (start: Date, resource?: IBigCalendarResource) => void;
  reloadTrigger?: boolean;
  resourceId?: string;
  currentActivityType?: string;
}

export const EditEventCalendarWraper: React.FunctionComponent<IEditEventCalendarWraperProps> = (
  props
) => {
  const appSettings = useAppSettings(api.settings);
  const isVehicleOnlyView = props.isVehicleOnlyView ?? false;
  const [events, setEvents] = useState<IBigCalendarEvent[]>([]);
  const [resources, setResources] = useState<IBigCalendarResource[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [shifts, setShifts] = useState<IShowroomShift[]>([]);
  useEffect(() => {
    setLoaded(true);
    setEvents([]);
    setResources([]);
  }, []);

  useEffect(() => {
    if (loaded) {
      reloadData();
    }
  }, [
    formatDate(props.selectedDate, DateFormats.NormalDateFormat),
    props.formFilter?.salesPerson,
    props.formFilter?.showRoom,
    props.formFilter?.vehicle,
  ]);

  useEffect(() => {
    if (loaded) {
      reloadData();
    }
  }, [props.reloadTrigger]);

  const reloadData = () => {
    setEvents([]);
    setResources([]);
    reload();
    // vehicleReload();
  };

  const getEventListType = () => {
    const eventType = props.eventType;
    if (eventType === "scheduleATestDrive") {
      return "test-drive";
    } else if (eventType === "requestACallBack") {
      return "request-call-back";
    } else if (
      eventType === "showroomAppointment" ||
      eventType === "scheduleAShowroomVisit"
    ) {
      return "showroom-visits";
    } else if (
      eventType === "call" ||
      eventType === "deadline" ||
      eventType === "meeting" ||
      eventType === "task"
    ) {
      return "";
    }
  };

  let minDate: any;
  if (
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity !== undefined &&
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity >= 0
  ) {
    minDate = new Date().setDate(
      new Date().getDate() -
        appSettings.data?.leadActivityAssignment
          ?.noOfDaysToAllowBackdatedActivity
    );
  }
  const settingsStartDate = combineDateAndTime(
    moment().startOf("day").toDate(),
    appSettings?.data?.application?.appointmentTime?.start || "12:00 AM"
  );

  const settingsEndDate = combineDateAndTime(
    moment().startOf("day").toDate(),
    appSettings?.data?.application?.appointmentTime?.end || "11:59 PM"
  );

  const { data: calendarEventResponse, reload, loading } = useDataLoader<
    CalendarApiResponse<ICalendarEventResponse>
  >(() => {
    if (isVehicleOnlyView) {
      return [];
    }
    const filter = createFilter();
    const listType = getEventListType();
    return api.lead.getCalandarEvents(filter, listType);
  });

  const createFilter = (): {
    fromDate?: Date;
    toDate?: Date;
    assignedPersonIds?: string[];
    showroomIds?: string[];
    vehicleIds?: string[];
    dateType?: string;
  } => {
    return {
      fromDate: moment(props.selectedDate).startOf("day").toDate(),
      toDate: moment(props.selectedDate).endOf("day").toDate(),
      assignedPersonIds: props.formFilter?.salesPerson
        ? [props.formFilter?.salesPerson]
        : undefined,
      showroomIds:
        props.formFilter?.showRoom != null
          ? [props.formFilter?.showRoom]
          : undefined,
      vehicleIds:
        props.formFilter?.vehicle != null
          ? [props.formFilter?.vehicle]
          : undefined,
      dateType: "Due Date",
    };
  };

  // const {
  //   data: vehicleEventResponse,
  //   reload: vehicleReload,
  //   loading: vehicleLoading,
  // } = useDataLoader<CalendarApiResponse<ICalendarEventResponse>>(() => {
  //   // if (!isVehicleOnlyView && !props.formFilter?.vehicle) {
  //   //   return {
  //   //     count: 0,
  //   //     appointments: [],
  //   //     assignees: [],
  //   //     vehicles: [],
  //   //   };
  //   // }
  //   return api.lead.getVehicleEvents(createVehicleFilter());
  // });
  const createVehicleFilter = (): {
    fromDate?: Date;
    toDate?: Date;
    assignedPersonIds?: string[];
    showroomIds?: string[];
    vehicleIds?: string[];
  } => {
    return {
      fromDate: moment(props.selectedDate).startOf("day").toDate(),
      toDate: moment(props.selectedDate).endOf("day").toDate(),
      showroomIds:
        props.formFilter?.showRoom != null
          ? [props.formFilter?.showRoom]
          : undefined,
      vehicleIds:
        props.formFilter?.vehicle != null
          ? [props.formFilter?.vehicle]
          : undefined,
    };
  };

  useEffect(() => {
    if (calendarEventResponse?.status === StatusCodes.OK) {
      const newCalendarEvents = parseCalendarEventsToBigCalendarEvents(
        calendarEventResponse?.data?.appointments ?? []
      );
      const currentResources = parseAssigneesBigCalendarResources(
        calendarEventResponse.data?.assignees ?? [],
        props?.formFilter?.showRoom != null ? [props?.formFilter?.showRoom] : []
      );
      const filter = createFilter();
      const showroomshifts = parseShowroomShifts(
        calendarEventResponse.data.showroomTimings ?? [],
        filter as any
      );
      parseAndSetShifts(showroomshifts);
      setResources([...resources].concat(currentResources));
      setEvents([...events].concat(newCalendarEvents));
    }
  }, [calendarEventResponse]);

  // useEffect(() => {
  //   if (vehicleEventResponse?.status === StatusCodes.OK) {
  //     const newCalendarEvents =
  //       isVehicleOnlyView || !!props.formFilter?.vehicle
  //         ? parseVehicleEventsToBigCalendarEvents(
  //             vehicleEventResponse?.data?.appointments ?? []
  //           )
  //         : [];
  //     const currentResources =
  //       isVehicleOnlyView || !!props.formFilter?.vehicle
  //         ? parseVehiclesBigCalendarResources(
  //             vehicleEventResponse.data?.vehicles ?? []
  //           )
  //         : [];
  //     if (props.formFilter?.vehicle) {
  //       const filteredResource = currentResources.filter(
  //         (p) => p.resourceId === props.formFilter?.vehicle
  //       );

  //       setResources(filteredResource.concat([...resources]));
  //     } else if (isVehicleOnlyView) {
  //       setResources(currentResources.concat([...resources]));
  //     }

  //     if (isVehicleOnlyView && props.tempEvent && props.tempEvent?.id == null) {
  //       newCalendarEvents.push(props.tempEvent);
  //     }
  //     setEvents(newCalendarEvents.concat([...events]));
  //   }
  // }, [vehicleEventResponse]);

  const getResource = (id: string) => {
    return resources.find((p) => p.resourceId === id);
  };

  const parseAndSetShifts = (showroomShifts: {
    [id: string]: IShowroomShift[];
  }) => {
    let tempShifts: IShowroomShift[] = [];
    for (const property in showroomShifts) {
      if (property) {
        tempShifts = tempShifts.concat(showroomShifts[property]);
      }
    }
    setShifts(tempShifts);
  };

  let canSelectDate = true;
  if (minDate && moment(minDate).isAfter(moment(props.selectedDate), "day")) {
    canSelectDate = false;
  }

  return (
    <Fragment>
      <div style={{ textAlign: "center" }} className="py-3">
        <CalendarHeaderDate
          currentView={"day"}
          onDateChange={(date: Date) => {
            props.onDateChange(date);
          }}
          date={props.selectedDate}
          sm
          minDate={minDate}
        ></CalendarHeaderDate>
      </div>
      <div className={"edit-event-popup"}>
        <BigCalendarComponent
          shiftTimings={shifts}
          onSlotClick={(start: Date, resourceId: string) => {
            if (props.onSlotClick) {
              props.onSlotClick(start, getResource(resourceId));
            }
          }}
          skipConfirm={true}
          tempEvent={props.tempEvent}
          height={"80vh"}
          minMax={{
            min: moment(new Date())
              .startOf("day")
              .set("hour", settingsStartDate.getHours())
              .set("minute", settingsStartDate.getMinutes())
              .toDate(),
            max: moment(new Date())
              .endOf("day")
              .set("hour", settingsEndDate.getHours())
              .set("minute", settingsEndDate.getMinutes())
              .toDate(),
          }}
          key={"event-edit-calendar"}
          onUpdateEvent={(event: IBigCalendarEvent, index: number) => {
            props.onSelectSlot(
              event.start,
              event.end,
              getResource(event.resourceId)
            );
          }}
          resourceHeader={(resource: IBigCalendarResource) => {
            if (resource.resourceType === "vehicle") {
              return (
                <CalendarVehicleHeader
                  resource={resource}
                ></CalendarVehicleHeader>
              );
            }
            return (
              <CalendarStaffImageOnlyHeader
                resource={resource}
              ></CalendarStaffImageOnlyHeader>
            );
          }}
          resourceMap={resources}
          events={events}
          currentView={"day"}
          onSelectSlot={(start: Date, end: Date, resourceId: string) => {
            props.onSelectSlot(start, end, getResource(resourceId));
          }}
          selectedDate={props.selectedDate}
          selectable={canSelectDate}
          currentActivityType={props.currentActivityType}
        ></BigCalendarComponent>
      </div>
      {loading && <ContainerFitLoader></ContainerFitLoader>}
    </Fragment>
  );
};
